import { useEffect, useState } from 'react';

import { useSearchParams } from 'react-router-dom';

import {
  Center,
  Flex,
  Input,
  InputGroup,
  InputRightElement,
  Spacer,
  Spinner,
  Text,
  useDisclosure,
} from '@chakra-ui/react';

import { PaginationState } from '@tanstack/react-table';

import {
  FileUploadStatus,
  FileUploadType,
  Filters,
  SearchNormalOutline,
  useToast,
} from '@aslanpay/portal-common-lib';

import AuthenticatedPageWrapper from 'app/components/UI/AuthenticatedPageWrapper';
import { Table } from 'app/components/UI/Table';
import { FileUploadDetailsModal } from 'app/components/UploadedFiles/FileUploadDetailsModal';

import { defaultPageSize } from 'app/constants/table';
import {
  FileUploadRow,
  FILE_UPLOAD_STATUS_OPTIONS,
  FILE_UPLOAD_TYPE_OPTIONS,
} from 'app/constants/fileUploads';

import useDebounce from 'app/hooks/useDebounce';
import useDownloadFile from 'app/hooks/useDownloadFile';
import useFetchFileUploadsPage from 'app/hooks/useFetchFileUploadsPage';

import { useManagingCompanyStore } from 'app/store';

import { getFileUploadColumns, FileUploadActions } from 'app/utils/table/fileUploads';

import type { Filter, FilterData, MultiSelectionData } from '@aslanpay/portal-common-lib';

const PAGE_SIZE_OPTIONS = [
  { value: 15, text: '15 rows / page' },
  { value: 25, text: '25 rows / page' },
  { value: 50, text: '50 rows / page' },
];

function UploadedFiles() {
  const company = useManagingCompanyStore((state) => state.company);
  const toast = useToast();

  const [searchParams, setSearchParams] = useSearchParams();

  const [searchQuery, setSearchQuery] = useState<string>(searchParams.get('query') || '');
  const [typeFilter, setTypeFilter] = useState<FileUploadType | undefined>(
    (searchParams.get('type') as FileUploadType) || undefined
  );
  const [statusFilter, setStatusFilter] = useState<FileUploadStatus | undefined>(
    (searchParams.get('status') as FileUploadStatus) || undefined
  );
  const [activeFilter, setActiveFilter] = useState<string | undefined>();
  const [downloadingId, setDownloadingId] = useState<string | undefined>();
  const [selectedFile, setSelectedFile] = useState<FileUploadRow | undefined>();

  const currentPage = searchParams.get('page') ? Number(searchParams.get('page')) - 1 : 0;
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: currentPage,
    pageSize: defaultPageSize,
  });

  const { isOpen: isDetailsOpen, onOpen: onOpenDetails, onClose: onCloseDetails } = useDisclosure();

  const resetPagination = () => setPagination({ pageIndex: 0, pageSize: defaultPageSize });

  const { data: fileUploads, isLoading } = useFetchFileUploadsPage({
    companyId: company?.id || '',
    type: typeFilter,
    status: statusFilter,
    name: searchQuery || undefined,
    page: pageIndex,
    size: pageSize,
  });

  const { mutate: downloadFile } = useDownloadFile({
    onSuccess: (downloadUrl) => window.open(downloadUrl, '_blank'),
    onError: () =>
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Download failed',
        description: 'Sorry, the file could not be downloaded. Please try again.',
      }),
    onSettled: () => setDownloadingId(undefined),
  });

  const handleDownload = (file: FileUploadRow) => {
    setDownloadingId(file.id);
    downloadFile({ fileId: file.id, fileName: file.name });
  };

  const handleViewDetails = (file: FileUploadRow) => {
    setSelectedFile(file);
    onOpenDetails();
  };

  const handleCopyId = (file: FileUploadRow) => {
    navigator.clipboard?.writeText(file.id);
    toast({
      position: 'top-right',
      status: 'success',
      title: 'Copied',
      description: 'File ID copied to clipboard.',
    });
  };

  const actions: FileUploadActions = {
    download: handleDownload,
    viewDetails: handleViewDetails,
    copyId: handleCopyId,
  };

  const filters: Filter[] = [
    {
      name: 'Type',
      type: 'category',
      data: FILE_UPLOAD_TYPE_OPTIONS,
      selectedValues: typeFilter ? [typeFilter] : [],
      singleSelection: true,
    },
    {
      name: 'Status',
      type: 'category',
      data: FILE_UPLOAD_STATUS_OPTIONS,
      selectedValues: statusFilter ? [statusFilter] : [],
      singleSelection: true,
    },
  ];

  const handleSearchQueryChange = (query: string) => {
    setSearchQuery(query);
    resetPagination();
  };

  const onSearchQueryChangedDebounced = useDebounce(handleSearchQueryChange);

  const handleFilterChange = (filterName: string, newValues: string[]) => {
    const value = newValues.at(-1);

    if (filterName === 'Type') {
      setTypeFilter((value as FileUploadType) || undefined);
      resetPagination();
    }

    if (filterName === 'Status') {
      setStatusFilter((value as FileUploadStatus) || undefined);
      resetPagination();
    }
  };

  const formatFilterValue = (_: string, filterValue: string | number, filterData: FilterData) =>
    (filterData as MultiSelectionData).find((option) => option.value === filterValue)?.text ||
    String(filterValue);

  useEffect(() => {
    setSearchParams(
      {
        page: `${pageIndex + 1}`,
        ...(searchQuery && { query: searchQuery }),
        ...(typeFilter && { type: typeFilter }),
        ...(statusFilter && { status: statusFilter }),
      },
      { replace: true }
    );
  }, [pageIndex, searchQuery, typeFilter, statusFilter, setSearchParams]);

  return (
    <AuthenticatedPageWrapper title="Uploaded files">
      <Flex gap="10px">
        <Text as="h2" textStyle="h2" color="brand.darkPurple">
          Uploaded files
        </Text>
      </Flex>

      <Flex justify="space-between" align="center" gap="10px">
        <Filters
          activeFilter={activeFilter}
          displayFormatter={formatFilterValue}
          filters={filters}
          onFilterClose={() => setActiveFilter(undefined)}
          onFilterChange={handleFilterChange}
          onFilterClick={(filterName) => setActiveFilter(filterName)}
        />
        <Spacer />
        <InputGroup maxW="278px" color="brand.darkGray">
          <Input
            onChange={(event) => onSearchQueryChangedDebounced(event.target.value)}
            defaultValue={searchQuery}
            type="text"
            placeholder="Search by file name"
          />
          <InputRightElement w="56px" h="full">
            <SearchNormalOutline boxSize="24px" />
          </InputRightElement>
        </InputGroup>
      </Flex>

      {isLoading ? (
        <Center width="full" height="full" flexGrow={1}>
          <Spinner color="brand.purple" size="xl" mb="50px" />
        </Center>
      ) : (
        <Table
          columns={getFileUploadColumns(actions, downloadingId)}
          data={(fileUploads?.list ?? []) as FileUploadRow[]}
          total={fileUploads?.total ?? 0}
          pageNumber={fileUploads?.pageNumber ?? pageIndex}
          pageSize={fileUploads?.pageSize ?? pageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          onPaginationChange={setPagination}
        />
      )}

      <FileUploadDetailsModal isOpen={isDetailsOpen} onClose={onCloseDetails} file={selectedFile} />
    </AuthenticatedPageWrapper>
  );
}

export default UploadedFiles;
